import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import CustomNoRowsOverlay from '../../Table/CustomNoRowsOverlay';
import { useAppSelector } from '../../../redux/hooks';
import { IActivityDetails } from '../../../redux/client/clientSlice';
import { formatDateTime } from '../../../utils/dateUtils';
import { AdvanceActivityStepType } from '../../../types/activity';
import { AssetType } from '../../../redux/activity/activitySlice';
import { slimScrollbarStyles } from '../../../utils/styles';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Voice from './Voice';

export interface PaginationModelChangeProps {
  pageSize: number;
  page: number;
}

interface IRenderCellParams {
  row: IActivityDetails;
  value: string | boolean;
}

interface IColumn {
  field: string;
  headerName: string;
  hide?: boolean;
  minWidth?: number;
  disableColumnMenu?: boolean;
  sortable?: boolean;
  flex?: number;
  renderHeader?: (_params: IRenderCellParams) => React.ReactNode;
  renderCell?: (_params: IRenderCellParams) => React.ReactNode;
}

export default function AnswersTable() {
  const [filteredData, setFilteredData] = useState<IActivityDetails[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const { t } = useTranslation();
  const [columnStepData, setColumnStepData] = useState<IColumn[]>([]);
  const activity = useAppSelector((state) => state.client.activity);
  const [rowCount, setRowCount] = useState<number>(0);

  function reorderActivities(data: IActivityDetails[]): IActivityDetails[] {
    const activitiesMap = new Map<string, IActivityDetails>();

    // Iterate through activities using array methods
    data.forEach((activity1) => {
      const { id, ...rest } = activity1;
      activitiesMap.set(id, { id, ...rest, ActivityStep: [] });

      // Sort and push steps directly within the loop
      activity1.ActivityStep.sort((a, b) => a.stepOrder - b.stepOrder);
      activitiesMap.get(id)!.ActivityStep.push(...activity1.ActivityStep);
    });

    return Array.from(activitiesMap.values());
  }

  useEffect(() => {
    const advancedActivityAnswers = activity.activityDetails
      .map((activityDetail) => {
        if (activityDetail.isAdvanceActivity) {
          // Filter ActivityStep objects based on isAdvanceStep
          const filteredActivityStep = activityDetail.ActivityStep.filter(
            (step) => step.isAdvanceStep
          );
          // Create a new object with filtered ActivityStep array and other properties
          return { ...activityDetail, ActivityStep: filteredActivityStep };
        }
        return null;
      })
      .filter(Boolean) // Remove any null entries from the array
      .sort(
        (a, b) => new Date(b?.completedAt || 0).getTime() - new Date(a?.completedAt || 0).getTime()
      );

    const sortedActivities = reorderActivities(advancedActivityAnswers as IActivityDetails[]);

    const stepData = sortedActivities[0]?.ActivityStep.map((step, index) => ({
      field: step.id,
      headerName: step.text,
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 156,
      renderHeader: () => (
        <Box display="flex" flexDirection="column" gap="2px">
          <Typography variant="caption2Bold" color="primary">
            {t('clientProgress.step')} {index + 1}
          </Typography>
          <Typography variant="meta" color="primary">
            {step.text}
          </Typography>
        </Box>
      ),
      renderCell: (params: IRenderCellParams) => {
        if (
          params.row.ActivityStep[index].advanceStepType === AdvanceActivityStepType.PERCENTAGE &&
          params.row.ActivityStep[index].ActivityStepResponse[0]
        ) {
          return (
            <Typography variant="bodyRegular" color="primary">
              {`${Math.round(
                Number(params.row.ActivityStep[index].ActivityStepResponse[0]?.asset)
              )}%`}
            </Typography>
          );
        }
        if (
          params.row.ActivityStep[index].advanceStepType === AdvanceActivityStepType.ANY &&
          params.row.ActivityStep[index].ActivityStepResponse[0]?.assetType === AssetType.IMAGE
        ) {
          const asset = params.row.ActivityStep[index].ActivityStepResponse[0]?.asset;
          return (
            <Box>
              <LazyLoadImage
                src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${asset}`}
                alt="activity response"
                effect="blur"
                style={{
                  width: '122px',
                  height: '175px',
                  borderRadius: '10px',
                }}
              />
            </Box>
          );
        }
        if (
          params.row.ActivityStep[index].advanceStepType === AdvanceActivityStepType.ANY &&
          params.row.ActivityStep[index].ActivityStepResponse[0]?.assetType === AssetType.VIDEO
        ) {
          const asset = params.row.ActivityStep[index].ActivityStepResponse[0]?.asset;
          return (
            <Box>
              <video
                src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${asset}`}
                controls
                style={{ width: '122px', height: '175px' }}
              >
                <track kind="captions" src="captions.vtt" label="English" />
              </video>
            </Box>
          );
        }
        if (
          params.row.ActivityStep[index].advanceStepType === AdvanceActivityStepType.ANY &&
          params.row.ActivityStep[index].ActivityStepResponse[0]?.assetType === AssetType.AUDIO
        ) {
          const asset = params.row.ActivityStep[index].ActivityStepResponse[0]?.asset;
          return <Voice message={asset} />;
        }
        if (
          params.row.ActivityStep[index].advanceStepType === AdvanceActivityStepType.ANY &&
          params.row.ActivityStep[index].ActivityStepResponse[0]?.assetType === AssetType.DOCUMENT
        ) {
          const asset = params.row.ActivityStep[index].ActivityStepResponse[0]?.asset;
          return (
            <Box>
              <iframe
                title={asset}
                src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${asset}`}
                style={{ width: '122px', height: '175px' }}
              >
                This browser does not support PDFs. Please download the PDF to view it.
              </iframe>
            </Box>
          );
        }
        return (
          <Typography
            variant="bodyRegular"
            color="primary"
            sx={{ overflow: 'auto', ...slimScrollbarStyles }}
          >
            {params.row.ActivityStep[index].ActivityStepResponse[0]?.asset}
          </Typography>
        );
      },
    }));
    if (Array.isArray(sortedActivities)) {
      setRowCount(sortedActivities.length);
      setFilteredData(sortedActivities as IActivityDetails[]);
    }
    setColumnStepData(stepData as IColumn[]);
  }, [activity, paginationModel]);

  const getRowClassName = () => 'no-border';

  const columns: IColumn[] = [
    { field: 'id', headerName: 'ID', hide: true },
    {
      field: 'completedAt',
      headerName: 'Date/Time',
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 156,
      renderHeader: () => (
        <Box display="flex">
          <Typography variant="meta" color="primary">
            {t('clientProgress.dateTime')}
          </Typography>
        </Box>
      ),
      renderCell: (params: IRenderCellParams) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="meta" color="primary">
            {formatDateTime(params.row.completedAt).formattedDay}
          </Typography>
          <Typography variant="bodyRegular" color="primary">
            {formatDateTime(params.row.completedAt).formattedTime}
          </Typography>
        </Box>
      ),
    },
    ...columnStepData,
  ];

  return (
    <Box height="100%" display="flex" flexDirection="column" bgcolor="#fff" borderRadius="10px">
      <Box
        flexGrow="1"
        height="100%"
        sx={{
          '&::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
          },
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
        }}
      >
        <DataGrid
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rows={filteredData}
          rowCount={rowCount}
          paginationMode="client"
          columns={columns as GridColDef[]}
          getRowClassName={getRowClassName}
          columnVisibilityModel={{
            id: false,
          }}
          pageSizeOptions={[10, 15, 20]}
          disableRowSelectionOnClick
          columnHeaderHeight={150}
          getRowHeight={() => 'auto'}
          //   loading={fetchClientsLoading || deleteClientLoading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          sx={{
            border: '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)',
            borderColor: '#FFDDC8',
            borderRadius: '10px',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)',
              paddingLeft: '24px',
              borderColor: '#FFDDC8',
              cursor: 'pointer',
              paddingY: '12px !important',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: (theme) => theme.palette.primary.main,
              fontWeight: 400,
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              paddingLeft: '14px',
              border: 'none',
              whiteSpace: 'normal',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderColor: '#FFDDC8',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: '#6CA3AC',
              borderTop: '1px solid #6CA3AC' /* Add border on top */,
            },
            '& .MuiTablePagination-root button': {
              mt: '4px',
              color: '#6CA3AC' /* Change text color */,
              '&:disabled': {
                opacity: 0.5,
              },
            },
            '& .MuiTablePagination-input': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
              ml: 0,
              pl: 0,
            },
            '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
              color: '#7CAEBE',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            },
            '& .MuiTablePagination-displayedRows': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
            },
            '& .MuiDataGrid-virtualScroller': {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '20px',
                border: '3px solid #f5f5f5',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#888',
              },
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Box>
    </Box>
  );
}
